import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import Session from 'supertokens-web-js/recipe/session';
import api from '@/lib/api';

let callbackProcessed = false;

const STASH_KEY = 'marketplace_callback_pending';

interface StashedCallback {
  code: string;
  state: string;
  at: number;
}

export default function MarketplaceCallback() {
  const { t } = useTranslation('marketplaceCallback');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState(t('loading.default'));

  useEffect(() => {
    if (callbackProcessed) return;
    callbackProcessed = true;

    (async () => {
      let code = params.get('code');
      let stateParam = params.get('state');
      const error = params.get('error');

      if (error) {
        setState('error');
        setMessage(t('error.authDenied', { error }));
        return;
      }

      if (!code || !stateParam) {
        const stashed = sessionStorage.getItem(STASH_KEY);
        if (stashed) {
          try {
            const parsed = JSON.parse(stashed) as StashedCallback;
            if (Date.now() - parsed.at < 15 * 60 * 1000) {
              code = parsed.code;
              stateParam = parsed.state;
            }
          } catch { /* ignore */ }
        }
      }

      if (!code || !stateParam) {
        setState('error');
        setMessage(t('error.missingCode'));
        return;
      }

      const sessionExists = await Session.doesSessionExist();
      if (!sessionExists) {
        const stash: StashedCallback = { code, state: stateParam, at: Date.now() };
        sessionStorage.setItem(STASH_KEY, JSON.stringify(stash));
        navigate(`/login?returnTo=${encodeURIComponent('/callback')}`, { replace: true });
        return;
      }

      try {
        if (stateParam.startsWith('alibaba_')) {
          await api.post('/admin/platform/settings/alibaba/oauth-exchange', {
            code,
            state: stateParam,
            redirect_uri: window.location.origin + '/callback',
          });
          sessionStorage.removeItem(STASH_KEY);
          setState('success');
          setMessage(t('success.alibaba'));
          setTimeout(() => navigate('/admin/platform/settings', { replace: true }), 1500);
          return;
        }

        setState('error');
        setMessage(t('error.unknownProvider', { provider: stateParam.split('_')[0] }));
      } catch (err: any) {
        sessionStorage.removeItem(STASH_KEY);
        setState('error');
        setMessage(err?.message || t('error.fallback'));
      }
    })();
    // `t` is stable across renders within the same locale; including it
    // would re-fire this side-effect (and re-trigger the OAuth exchange)
    // on locale change, which is unsafe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
        {state === 'loading' && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-sage-700 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-stone-900 mb-2">{t('loading.heading')}</h1>
            <p className="text-sm text-stone-500">{message}</p>
          </>
        )}
        {state === 'success' && (
          <>
            <CheckCircle className="w-10 h-10 text-sage-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-stone-900 mb-2">{t('success.heading')}</h1>
            <p className="text-sm text-stone-500">{message}</p>
            <p className="text-xs text-stone-400 mt-2">{t('success.redirecting')}</p>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="w-10 h-10 text-red-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-stone-900 mb-2">{t('error.heading')}</h1>
            <p className="text-sm text-red-600 mb-4">{message}</p>
            <button
              onClick={() => navigate('/admin/platform/settings', { replace: true })}
              className="text-sm text-sage-700 underline"
            >
              {t('error.back')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
